import React, { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import DropdownNode from '../DropdownNode/DropdownNode';
import DropdownNodeDisabled from '../DropdownNodeDisabled/DropdownNodeDisabled';
import { INTERNAL_NOTE } from 'constants/messageTypes';
import { ESCALATION_REASON, SPECIALIST_ONLY_HINT } from 'constants/noteTypes';
import { StyledNodesContainer, StyledPointer } from './RatingDropdown.styles';
import { useSelector } from 'react-redux';
import { isAPartnerProtector } from 'extractFromState';
import { SOURCE_TYPE_SIMPLR, SOURCE_TYPE_SIMPLR_BOT } from '../../../constants/messageStatuses';

const getExpertsListForDropdown = (expertsList, conversationObj = {}, obfuscate) => {
  if (!isArray(expertsList) || isEmpty(conversationObj) || !isArray(conversationObj.allMessages)) {
    return [];
  }

  const { allMessages, ...messages } = conversationObj;
  const expertsRepliedList = [];

  expertsList.forEach((expert) => {
    // We have autoresponses sent on behalf of the expert, but with the source “SIMPLR_BOT”,
    // and messages sent by the expert with the source “SIMPLR”.
    // To select the correct message, first try to find for the source: “SIMPLR”, and then for the source: “SIMPLR_BOT”
    const messagesWithSourceSimplr = Object.values(messages).filter(
      (message) => message.expert?.simplrExpertId === expert.value && message.source === SOURCE_TYPE_SIMPLR
    );
    const messagesWithSourceSimplrBot = Object.values(messages).filter(
      (message) => message.expert?.simplrExpertId === expert.value && message.source === SOURCE_TYPE_SIMPLR_BOT
    );
    // Find a message, that is not an INTERNAL_NOTE, that the current expert sent.
    const currentExpertsMessage = [...messagesWithSourceSimplr, ...messagesWithSourceSimplrBot].find((message) => {
      const isSimplrBot = get(message, 'expert.isSystemExpert') || get(message, 'expert.isSimplrBot');
      const obfuscatedName = get(message, 'expert.obfuscatedName');

      // For experts (not bots), we need an obfuscated name in order to allow them to be rated.
      if (!isSimplrBot && !obfuscatedName) {
        return false;
      }

      const isInternalNoteType =
        message.messageType === INTERNAL_NOTE && [SPECIALIST_ONLY_HINT, ESCALATION_REASON].includes(message.noteType);
      // There also exists messages that are automated notes but messageType isn't INTERNAL_NOTE
      const isNote = message.messageType !== INTERNAL_NOTE && [ESCALATION_REASON].includes(message.noteType);

      const messageIsInvalidNoteType = isInternalNoteType || isNote;

      return !messageIsInvalidNoteType;
    });

    // if the expert message exists, add the expert to the dropdown
    if (currentExpertsMessage) {
      if (obfuscate) {
        const message = messages[currentExpertsMessage.messageId];
        // obfuscatedName exists only for experts messages, so we can use fallback a value for bots
        expert.label = get(message, 'expert.obfuscatedName', 'Bot');
      }
      expertsRepliedList.push(expert);
    }
  });

  return expertsRepliedList;
};

export const RatingDropdown = ({
  ratingToolConfig = {},
  ratingScore = null,
  expertsObject,
  allMessages,
  currentlyActiveNode,
  ratingScoreDetails = {},
  selectedExpert = {},
  setRatingScoreDetails,
  setSelectedExpert,
  setCurrentlyActiveNode,
}) => {
  const [totalNodesCount, setTotalNodesCount] = useState(0);

  // TODO: Fix dependency array
  useEffect(() => {
    const nodesCount = getTemplateKeys(ratingToolConfig, ratingScore || 1).length - 1;
    setTotalNodesCount(nodesCount);
  }, [ratingToolConfig, ratingScore]); // eslint-disable-line

  const isUserAPartnerProtector = useSelector(isAPartnerProtector);
  const expertsListForDropdown = getExpertsListForDropdown(expertsObject, allMessages, isUserAPartnerProtector);
  const getTemplateKeys = (template, rating) => Object.keys(getTemplate(template, rating) || {});
  const getTemplate = (template, rating) => template[rating];

  const isPreviouslyNodeVisible = (thisNodeOrder, nodeTitle) => {
    return thisNodeOrder < currentlyActiveNode && !!ratingScoreDetails[nodeTitle];
  };

  return (
    <>
      <StyledPointer ratingScore={ratingScore} />
      <StyledNodesContainer>
        {getTemplateKeys(ratingToolConfig, ratingScore).map((nodeTitle, index) => {
          if (isPreviouslyNodeVisible(index, nodeTitle)) {
            return (
              <DropdownNodeDisabled
                key={nodeTitle}
                nodeTitle={nodeTitle}
                selectedExpert={selectedExpert}
                expertList={expertsListForDropdown}
                ratingScoreDetails={ratingScoreDetails}
              />
            );
          }

          if (index === currentlyActiveNode) {
            return (
              <DropdownNode
                key={nodeTitle}
                nodesOrder={{
                  currentlyActiveNode,
                  totalNodesCount,
                }}
                nodeTitle={nodeTitle}
                template={getTemplate(ratingToolConfig, ratingScore)[nodeTitle]}
                ratingScoreDetails={ratingScoreDetails}
                expertsList={expertsListForDropdown}
                selectedExpert={selectedExpert}
                setRatingScoreDetails={setRatingScoreDetails}
                setSelectedExpert={setSelectedExpert}
                setCurrentlyActiveNode={setCurrentlyActiveNode}
              />
            );
          }

          return null;
        })}
      </StyledNodesContainer>
    </>
  );
};

export default RatingDropdown;
